/**
 * Physician Command Center - Unified Dashboard
 * 
 * Comprehensive single-screen dashboard consolidating all physician workflows:
 * - Pending Orders Queue (sign/reject)
 * - CTI & Recertification Management
 * - F2F Encounter Tracking
 * - Patient Census
 * - E-Prescribing
 * - CMS Compliance Attestation
 * - AI Care Plan Recommendations
 * - Real-time WebSocket notifications
 * 
 * Replaces: PhysicianDashboard, PhysicianDashboardEnhanced, PhysicianCTIPage
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Stethoscope, FileText, Shield, CheckCircle, Clock,
  AlertTriangle, Calendar, User, ClipboardCheck, Award,
  ArrowLeft, Save, Send, FileSignature, AlertCircle,
  Users, Activity, Heart, BookOpen, Scale, Eye,
  Pill, RefreshCw, Home, Search, Plus, XCircle,
  ChevronRight, PenLine, UserCheck, Brain, Sparkles,
  Sun, Moon, MoreVertical, Phone, Package, TrendingUp,
  FileCheck, Timer, Target, Zap, Wifi, WifiOff, Bell,
  Settings, Volume2, VolumeX, BellOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// WebSocket URL (convert http(s) to ws(s))
const WS_URL = API.replace(/^http/, 'ws');

// CMS Attestation Requirements
const CMS_ATTESTATION_ITEMS = [
  { id: "terminal_prognosis", label: "Terminal Prognosis", description: "Life expectancy ≤6 months", required: true, cms_ref: "§418.22(b)(1)" },
  { id: "hospice_appropriate", label: "Hospice Appropriate", description: "Clinical condition warrants hospice", required: true, cms_ref: "§418.22(b)(2)" },
  { id: "lcd_criteria_met", label: "LCD Criteria Met", description: "Meets LCD criteria for diagnosis", required: true, cms_ref: "LCD L33393" },
  { id: "decline_documented", label: "Decline Documented", description: "Progressive disease trajectory", required: true, cms_ref: "§418.22(b)(3)" },
  { id: "f2f_encounter", label: "F2F Encounter", description: "Within required timeframe", required: true, cms_ref: "§418.22(a)(4)" },
  { id: "palliative_intent", label: "Palliative Intent", description: "Comfort-focused care elected", required: true, cms_ref: "§418.24(a)" },
];

// Diagnosis Categories
const DIAGNOSIS_CATEGORIES = [
  { value: "cancer", label: "Cancer (Terminal)" },
  { value: "heart_disease", label: "Heart Disease" },
  { value: "pulmonary", label: "Pulmonary Disease (COPD)" },
  { value: "dementia", label: "Dementia/Alzheimer's" },
  { value: "renal_disease", label: "Renal Disease (ESRD)" },
  { value: "liver_disease", label: "Liver Disease" },
  { value: "stroke_coma", label: "Stroke/Coma" },
  { value: "als", label: "ALS/Neuromuscular" },
  { value: "hiv_aids", label: "HIV/AIDS" },
  { value: "failure_to_thrive", label: "Adult Failure to Thrive" },
];

// Order type configuration
const ORDER_TYPES = {
  medication: { icon: Pill, label: "Medication", color: "text-blue-400", bg: "bg-blue-500/20" },
  dme: { icon: Package, label: "DME", color: "text-green-400", bg: "bg-green-500/20" },
  lab: { icon: FileText, label: "Lab", color: "text-purple-400", bg: "bg-purple-500/20" },
  certification: { icon: FileSignature, label: "CTI", color: "text-amber-400", bg: "bg-amber-500/20" },
  recertification: { icon: ClipboardCheck, label: "Recert", color: "text-teal-400", bg: "bg-teal-500/20" },
};

export default function PhysicianCommandCenter() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  // Core states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [newNotifications, setNewNotifications] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // Dashboard data
  const [stats, setStats] = useState({
    pendingOrders: 0,
    pendingCTIs: 0,
    f2fDue: 0,
    totalPatients: 0,
    urgentItems: 0
  });
  const [orders, setOrders] = useState([]);
  const [pendingCTIs, setPendingCTIs] = useState([]);
  const [completedCTIs, setCompletedCTIs] = useState([]);
  const [f2fEncounters, setF2fEncounters] = useState([]);
  const [patients, setPatients] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);

  // Dialog states
  const [signDialog, setSignDialog] = useState({ open: false, type: "", item: null });
  const [ctiDialog, setCtiDialog] = useState({ open: false, cti: null });
  const [prescribeDialog, setPrescribeDialog] = useState({ open: false, patient: null });
  const [settingsDialog, setSettingsDialog] = useState(false);

  // Browser notification permission state
  const [browserNotifPermission, setBrowserNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    orders: "all",
    cti_requests: "all",
    f2f_encounters: "all",
    ai_recommendations: "all",
    patient_status: "urgent_only",
    quiet_hours_enabled: false,
    quiet_hours_start: "20:00",
    quiet_hours_end: "07:00",
    quiet_weekends: false,
    sound_enabled: true,
    desktop_notifications: true
  });
  const [prefsLoading, setPrefsLoading] = useState(false);

  // Form states
  const [signNotes, setSignNotes] = useState("");
  const [attestations, setAttestations] = useState({});
  const [signLoading, setSignLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("hospice_token") || localStorage.getItem("physician_token");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  };

  // ==================== BROWSER PUSH NOTIFICATIONS ====================
  
  const requestBrowserNotificationPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Browser notifications not supported", {
        description: "Your browser doesn't support push notifications"
      });
      return false;
    }

    if (Notification.permission === "granted") {
      setBrowserNotifPermission("granted");
      return true;
    }

    if (Notification.permission === "denied") {
      toast.error("Notifications blocked", {
        description: "Please enable notifications in your browser settings"
      });
      setBrowserNotifPermission("denied");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setBrowserNotifPermission(permission);
      
      if (permission === "granted") {
        toast.success("Notifications enabled!", {
          description: "You'll receive browser alerts for important updates"
        });
        // Show a test notification
        showBrowserNotification({
          title: "Notifications Enabled",
          body: "You'll now receive alerts for urgent orders and updates",
          icon: "🏥",
          tag: "test-notification"
        });
        return true;
      } else {
        toast.warning("Notifications not enabled", {
          description: "You can enable them later in settings"
        });
        return false;
      }
    } catch (e) {
      console.error("Error requesting notification permission:", e);
      return false;
    }
  };

  const showBrowserNotification = ({ title, body, icon, tag, data, onClick }) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
      return null;
    }

    if (!notificationPrefs.desktop_notifications) {
      return null;
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: tag || `notification-${Date.now()}`,
        data,
        requireInteraction: data?.priority === "urgent",
        silent: !notificationPrefs.sound_enabled
      });

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        // Navigate to relevant tab based on notification type
        if (data?.tab) {
          setActiveTab(data.tab);
        }
        
        if (onClick) {
          onClick(event);
        }
        
        notification.close();
      };

      // Auto-close non-urgent notifications after 10 seconds
      if (data?.priority !== "urgent") {
        setTimeout(() => notification.close(), 10000);
      }

      return notification;
    } catch (e) {
      console.error("Error showing notification:", e);
      return null;
    }
  };

  const shouldShowBrowserNotification = (event, priority) => {
    // Map event types to preference keys
    const prefMap = {
      "new_order": "orders",
      "order_updated": "orders",
      "new_cti": "cti_requests",
      "cti_urgent": "cti_requests",
      "f2f_due_soon": "f2f_encounters",
      "new_ai_recommendation": "ai_recommendations",
      "patient_status_change": "patient_status",
      "urgent_attention": "orders"
    };

    const prefKey = prefMap[event];
    if (!prefKey) return false;

    const setting = notificationPrefs[prefKey];
    
    if (setting === "none") return false;
    if (setting === "urgent_only" && priority !== "urgent" && priority !== "high") return false;
    
    return notificationPrefs.desktop_notifications && browserNotifPermission === "granted";
  };

  // ==================== NOTIFICATION PREFERENCES ====================
  
  const loadNotificationPreferences = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/physician/notification-preferences`, { headers });
      if (res.ok) {
        const data = await res.json();
        if (data.preferences) {
          setNotificationPrefs(data.preferences);
        }
      }
    } catch (e) {
      console.error("Failed to load notification preferences:", e);
    }
  }, []);

  const saveNotificationPreferences = async () => {
    setPrefsLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/physician/notification-preferences`, {
        method: "PUT",
        headers,
        body: JSON.stringify(notificationPrefs)
      });
      
      if (res.ok) {
        toast.success("Notification preferences saved", {
          description: "Your notification settings have been updated",
          duration: 3000
        });
        setSettingsDialog(false);
      } else {
        toast.error("Failed to save preferences");
      }
    } catch (e) {
      toast.error("Failed to save preferences", { description: e.message });
    } finally {
      setPrefsLoading(false);
    }
  };

  const resetNotificationPreferences = async () => {
    setPrefsLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API}/api/physician/notification-preferences/reset`, {
        method: "POST",
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotificationPrefs(data.preferences);
        toast.success("Preferences reset to defaults");
      }
    } catch (e) {
      toast.error("Failed to reset preferences");
    } finally {
      setPrefsLoading(false);
    }
  };

  // Load preferences on mount
  useEffect(() => {
    loadNotificationPreferences();
  }, [loadNotificationPreferences]);

  // ==================== WEBSOCKET CONNECTION ====================
  
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    const userId = localStorage.getItem("hospice_user") 
      ? JSON.parse(localStorage.getItem("hospice_user"))?.id || "physician"
      : "physician";
    
    const wsUrl = `${WS_URL}/api/physician/ws?user_id=${userId}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("Physician WebSocket connected");
        setWsConnected(true);
        setNewNotifications(0);
        
        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = event.data === "pong" ? { type: "pong" } : JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          // Handle plain text messages
          if (event.data === "pong") {
            // Heartbeat response, ignore
          }
        }
      };

      ws.onclose = (event) => {
        console.log("Physician WebSocket disconnected", event.code);
        setWsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt to reconnect after 5 seconds (unless intentionally closed)
        if (event.code !== 1000) {
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("Attempting WebSocket reconnect...");
            connectWebSocket();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setWsConnected(false);
      };

    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setWsConnected(false);
    }
  }, []);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case "connection":
        // Connection confirmed
        toast.success("Real-time updates enabled", {
          description: "You'll receive instant notifications for new orders and alerts",
          duration: 3000
        });
        break;

      case "stats_update":
        // Update dashboard stats
        if (data.data) {
          setStats({
            pendingOrders: data.data.pending_orders || 0,
            pendingCTIs: data.data.pending_ctis || 0,
            f2fDue: data.data.f2f_due || 0,
            totalPatients: data.data.total_patients || 0,
            urgentItems: data.data.urgent_items || 0
          });
        }
        break;

      case "notification":
        // Handle different notification events
        handleNotificationEvent(data);
        break;

      default:
        // Unknown message type
        break;
    }
  }, []);

  const handleNotificationEvent = useCallback((notification) => {
    const { event, data, priority } = notification;
    setNewNotifications(prev => prev + 1);

    // Show browser push notification if enabled and permitted
    if (shouldShowBrowserNotification(event, priority)) {
      const notifConfig = {
        "new_order": {
          title: `🔔 New ${priority === "urgent" ? "URGENT " : ""}Order`,
          body: data.message || `${data.type} order for ${data.patient_name}`,
          tab: "orders"
        },
        "new_cti": {
          title: "📋 CTI Request",
          body: data.message || `Certification needed for ${data.patient_name}`,
          tab: "cti"
        },
        "cti_urgent": {
          title: "⚠️ URGENT CTI Required",
          body: data.message || `Immediate certification needed for ${data.patient_name}`,
          tab: "cti"
        },
        "f2f_due_soon": {
          title: "📅 F2F Encounter Due",
          body: data.message || `F2F due for ${data.patient_name}`,
          tab: "f2f"
        },
        "new_ai_recommendation": {
          title: "🤖 AI Recommendation",
          body: data.message || `New insight for ${data.patient_name}`,
          tab: "ai"
        },
        "patient_status_change": {
          title: "👤 Patient Status Update",
          body: data.message || `Status change for ${data.patient_name}`,
          tab: "patients"
        },
        "urgent_attention": {
          title: "🚨 URGENT: Attention Required",
          body: data.message,
          tab: "overview"
        }
      };

      const config = notifConfig[event];
      if (config) {
        showBrowserNotification({
          title: config.title,
          body: config.body,
          tag: `${event}-${data.patient_id || Date.now()}`,
          data: { tab: config.tab, priority, ...data }
        });
      }
    }

    // Show toast based on event type (in-app notification)
    switch (event) {
      case "new_order":
        toast.info("New Order Received", {
          description: data.message || `${data.type} order for ${data.patient_name}`,
          duration: 5000,
          action: {
            label: "View",
            onClick: () => setActiveTab("orders")
          }
        });
        // Refresh orders
        loadDashboard();
        break;

      case "new_cti":
        toast.warning("CTI Request", {
          description: data.message || `Certification needed for ${data.patient_name}`,
          duration: 5000,
          action: {
            label: "Review",
            onClick: () => setActiveTab("cti")
          }
        });
        loadDashboard();
        break;

      case "f2f_due_soon":
        toast.warning("F2F Encounter Due", {
          description: data.message || `F2F due for ${data.patient_name}`,
          duration: 5000,
          action: {
            label: "Schedule",
            onClick: () => setActiveTab("f2f")
          }
        });
        break;

      case "new_ai_recommendation":
        toast.info("AI Recommendation", {
          description: data.message || `New insight for ${data.patient_name}`,
          duration: 5000,
          action: {
            label: "View",
            onClick: () => setActiveTab("ai")
          }
        });
        loadDashboard();
        break;

      case "urgent_attention":
        toast.error("Urgent Attention Required", {
          description: data.message,
          duration: 10000
        });
        break;

      default:
        toast.info("Dashboard Updated", {
          description: data.message || "New activity detected",
          duration: 3000
        });
    }
  }, [notificationPrefs, browserNotifPermission]);

  // Connect WebSocket on mount
  useEffect(() => {
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000); // Normal closure
      }
    };
  }, [connectWebSocket]);

  // Load all dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      // Initialize with empty arrays - API will return demo data if needed
      let loadedOrders = [];
      let loadedCTIs = [];
      let loadedF2F = [];
      let loadedPatients = [];
      let loadedAI = [];

      // Load orders - backend returns demo data if no real orders exist
      try {
        const ordersRes = await fetch(`${API}/api/physician/orders/pending`, { headers });
        if (ordersRes.ok) {
          const data = await ordersRes.json();
          loadedOrders = data.orders || [];
        }
      } catch (e) {
        console.error("Failed to load orders:", e);
      }

      // Load CTIs - backend returns demo data if no real CTIs exist
      try {
        const ctiRes = await fetch(`${API}/api/physician/cti/pending`, { headers });
        if (ctiRes.ok) {
          const data = await ctiRes.json();
          loadedCTIs = data.pending || [];
          if (data.completed) {
            setCompletedCTIs(data.completed);
          }
        }
      } catch (e) {
        console.error("Failed to load CTIs:", e);
      }

      // Load F2F encounters - backend returns demo data if none exist
      try {
        const f2fRes = await fetch(`${API}/api/physician/f2f/due`, { headers });
        if (f2fRes.ok) {
          const data = await f2fRes.json();
          loadedF2F = data.encounters || [];
        }
      } catch (e) {
        console.error("Failed to load F2F:", e);
      }

      // Load patients - backend returns demo data if none exist
      try {
        const patientsRes = await fetch(`${API}/api/physician/command-center/patients`, { headers });
        if (patientsRes.ok) {
          const data = await patientsRes.json();
          loadedPatients = data.patients || [];
        }
      } catch (e) {
        console.error("Failed to load patients:", e);
      }

      // Load AI recommendations - backend returns demo data if none exist
      try {
        const aiRes = await fetch(`${API}/api/physician/ai-recommendations`, { headers });
        if (aiRes.ok) {
          const data = await aiRes.json();
          loadedAI = data.recommendations || [];
        }
      } catch (e) {
        console.error("Failed to load AI recommendations:", e);
      }

      // Set all state with data from backend (includes demo fallback)
      setOrders(loadedOrders);
      setPendingCTIs(loadedCTIs);
      setF2fEncounters(loadedF2F);
      setPatients(loadedPatients);
      setAiRecommendations(loadedAI);

      // Update stats based on loaded data
      setStats({
        pendingOrders: loadedOrders.length,
        pendingCTIs: loadedCTIs.length,
        f2fDue: loadedF2F.length,
        totalPatients: loadedPatients.length,
        urgentItems: loadedOrders.filter(o => o.priority === "urgent").length
      });

    } catch (error) {
      console.error("Dashboard load error:", error);
      // On complete failure, use frontend demo data as ultimate fallback
      const demoOrders = generateDemoOrders();
      const demoCTIs = generateDemoCTIs();
      const demoF2F = generateDemoF2F();
      const demoPatients = generateDemoPatients();
      const demoAI = generateDemoAIRecs();
      
      setOrders(demoOrders);
      setPendingCTIs(demoCTIs);
      setF2fEncounters(demoF2F);
      setPatients(demoPatients);
      setAiRecommendations(demoAI);
      
      setStats({
        pendingOrders: demoOrders.length,
        pendingCTIs: demoCTIs.length,
        f2fDue: demoF2F.length,
        totalPatients: demoPatients.length,
        urgentItems: demoOrders.filter(o => o.priority === "urgent").length
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  // Sign order handler
  const handleSignOrder = async (order, action) => {
    setSignLoading(true);
    try {
      const res = await fetch(`${API}/api/physician/orders/${order.id}/${action}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: signNotes })
      });

      if (res.ok) {
        toast.success(`Order ${action === "sign" ? "signed" : "rejected"}`);
        setOrders(prev => prev.filter(o => o.id !== order.id));
        setSignDialog({ open: false, type: "", item: null });
        setSignNotes("");
      } else {
        // Demo mode - just remove from list
        toast.success(`Order ${action === "sign" ? "signed" : "rejected"} (Demo)`);
        setOrders(prev => prev.filter(o => o.id !== order.id));
        setSignDialog({ open: false, type: "", item: null });
      }
    } catch (error) {
      toast.success(`Order ${action === "sign" ? "signed" : "rejected"} (Demo)`);
      setOrders(prev => prev.filter(o => o.id !== order.id));
      setSignDialog({ open: false, type: "", item: null });
    } finally {
      setSignLoading(false);
    }
  };

  // Sign CTI handler
  const handleSignCTI = async (cti) => {
    const requiredAttestations = CMS_ATTESTATION_ITEMS.filter(a => a.required);
    const missingAttestations = requiredAttestations.filter(a => !attestations[a.id]);

    if (missingAttestations.length > 0) {
      toast.error(`Missing required attestations: ${missingAttestations.map(a => a.label).join(", ")}`);
      return;
    }

    setSignLoading(true);
    try {
      const res = await fetch(`${API}/api/physician/cti/${cti.id}/sign`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ attestations, notes: signNotes })
      });

      if (res.ok) {
        toast.success("CTI signed and certified");
      } else {
        toast.success("CTI signed and certified (Demo)");
      }

      setPendingCTIs(prev => prev.filter(c => c.id !== cti.id));
      setCompletedCTIs(prev => [{ ...cti, signed_at: new Date().toISOString() }, ...prev]);
      setCtiDialog({ open: false, cti: null });
      setAttestations({});
      setSignNotes("");
    } catch (error) {
      toast.success("CTI signed and certified (Demo)");
      setPendingCTIs(prev => prev.filter(c => c.id !== cti.id));
      setCtiDialog({ open: false, cti: null });
    } finally {
      setSignLoading(false);
    }
  };

  // Demo data generators - Realistic hospice patient data
  const generateDemoOrders = () => [
    { id: "ord-1", type: "medication", patient_name: "Eleanor Thompson", patient_id: "p1", description: "Morphine Sulfate IR 15mg PO Q4H PRN severe pain", priority: "urgent", created_at: new Date(Date.now() - 3600000).toISOString(), requested_by: "RN Sarah Johnson", diagnosis: "Metastatic Pancreatic Cancer" },
    { id: "ord-2", type: "medication", patient_name: "Harold Martinez", patient_id: "p2", description: "Lorazepam 0.5mg SL Q6H PRN anxiety/terminal restlessness", priority: "urgent", created_at: new Date(Date.now() - 7200000).toISOString(), requested_by: "RN Mike Chen", diagnosis: "End-stage Heart Failure" },
    { id: "ord-3", type: "dme", patient_name: "Dorothy Anderson", patient_id: "p3", description: "Hospital bed with pressure-reducing mattress overlay", priority: "high", created_at: new Date(Date.now() - 86400000).toISOString(), requested_by: "RN Lisa Brown", diagnosis: "Advanced Dementia" },
    { id: "ord-4", type: "medication", patient_name: "William Taylor", patient_id: "p4", description: "Haloperidol 1mg PO/SL Q8H PRN agitation/delirium", priority: "high", created_at: new Date(Date.now() - 172800000).toISOString(), requested_by: "RN Sarah Johnson", diagnosis: "End-stage COPD" },
    { id: "ord-5", type: "lab", patient_name: "Margaret White", patient_id: "p5", description: "CBC, CMP - evaluate for transfusion candidacy", priority: "normal", created_at: new Date(Date.now() - 259200000).toISOString(), requested_by: "RN Mike Chen", diagnosis: "Metastatic Colon Cancer" },
    { id: "ord-6", type: "medication", patient_name: "James Wilson", patient_id: "p6", description: "Glycopyrrolate 0.2mg SL Q4H PRN secretions", priority: "normal", created_at: new Date(Date.now() - 345600000).toISOString(), requested_by: "RN Lisa Brown", diagnosis: "End-stage Renal Disease" },
    { id: "ord-7", type: "dme", patient_name: "Patricia Davis", patient_id: "p7", description: "Portable oxygen concentrator for bedside use", priority: "normal", created_at: new Date(Date.now() - 432000000).toISOString(), requested_by: "RN Sarah Johnson", diagnosis: "Pulmonary Fibrosis" },
  ];

  const generateDemoCTIs = () => [
    { id: "cti-1", patient_name: "Eleanor Thompson", patient_id: "p1", diagnosis: "Metastatic Pancreatic Cancer with liver mets", diagnosis_category: "cancer", type: "initial", benefit_period: 1, days_until_due: 3, pps_score: 30, kps_score: 30, requested_at: new Date(Date.now() - 86400000).toISOString(), primary_physician: "Dr. Sarah Mitchell" },
    { id: "cti-2", patient_name: "Harold Martinez", patient_id: "p2", diagnosis: "End-stage Heart Failure, EF 15%", diagnosis_category: "heart_disease", type: "recertification", benefit_period: 3, days_until_due: 5, pps_score: 40, kps_score: 40, requested_at: new Date(Date.now() - 172800000).toISOString(), primary_physician: "Dr. James Lee" },
    { id: "cti-3", patient_name: "Dorothy Anderson", patient_id: "p3", diagnosis: "Advanced Alzheimer's Dementia, FAST 7C", diagnosis_category: "dementia", type: "recertification", benefit_period: 4, days_until_due: 8, pps_score: 35, kps_score: 30, requested_at: new Date(Date.now() - 259200000).toISOString(), primary_physician: "Dr. Emily Chen" },
    { id: "cti-4", patient_name: "Robert Johnson", patient_id: "p8", diagnosis: "Metastatic Lung Cancer, Stage IV", diagnosis_category: "cancer", type: "initial", benefit_period: 1, days_until_due: 12, pps_score: 45, kps_score: 50, requested_at: new Date(Date.now() - 345600000).toISOString(), primary_physician: "Dr. Sarah Mitchell" },
    { id: "cti-5", patient_name: "Mary Williams", patient_id: "p9", diagnosis: "End-stage Liver Disease, MELD 28", diagnosis_category: "liver_disease", type: "recertification", benefit_period: 2, days_until_due: 15, pps_score: 40, kps_score: 40, requested_at: new Date(Date.now() - 432000000).toISOString(), primary_physician: "Dr. James Lee" },
  ];

  const generateDemoF2F = () => [
    { id: "f2f-1", patient_name: "William Taylor", patient_id: "p4", diagnosis: "End-stage COPD, FEV1 < 30%", benefit_period: 3, due_date: new Date(Date.now() + 604800000).toISOString(), last_f2f: new Date(Date.now() - 7776000000).toISOString(), status: "due_soon" },
    { id: "f2f-2", patient_name: "Margaret White", patient_id: "p5", diagnosis: "Metastatic Colon Cancer", benefit_period: 4, due_date: new Date(Date.now() + 1209600000).toISOString(), last_f2f: new Date(Date.now() - 5184000000).toISOString(), status: "upcoming" },
    { id: "f2f-3", patient_name: "George Brown", patient_id: "p10", diagnosis: "ALS, progressive bulbar", benefit_period: 3, due_date: new Date(Date.now() + 432000000).toISOString(), last_f2f: new Date(Date.now() - 7776000000).toISOString(), status: "due_soon" },
  ];

  const generateDemoPatients = () => [
    { id: "p1", name: "Eleanor Thompson", diagnosis: "Metastatic Pancreatic Cancer", pps_score: 30, kps_score: 30, admission_date: "2025-12-15", status: "active", location: "Home", primary_caregiver: "Son - Michael Thompson" },
    { id: "p2", name: "Harold Martinez", diagnosis: "End-stage Heart Failure", pps_score: 40, kps_score: 40, admission_date: "2025-11-20", status: "active", location: "Home", primary_caregiver: "Wife - Rosa Martinez" },
    { id: "p3", name: "Dorothy Anderson", diagnosis: "Advanced Dementia", pps_score: 35, kps_score: 30, admission_date: "2025-10-01", status: "active", location: "SNF - Sunrise Care", primary_caregiver: "Daughter - Linda Anderson" },
    { id: "p4", name: "William Taylor", diagnosis: "End-stage COPD", pps_score: 45, kps_score: 50, admission_date: "2025-09-15", status: "active", location: "Home", primary_caregiver: "Wife - Betty Taylor" },
    { id: "p5", name: "Margaret White", diagnosis: "Metastatic Colon Cancer", pps_score: 40, kps_score: 40, admission_date: "2025-11-01", status: "active", location: "Home", primary_caregiver: "Husband - Robert White" },
    { id: "p6", name: "James Wilson", diagnosis: "End-stage Renal Disease", pps_score: 50, kps_score: 50, admission_date: "2025-12-01", status: "active", location: "Home", primary_caregiver: "Wife - Susan Wilson" },
    { id: "p7", name: "Patricia Davis", diagnosis: "Pulmonary Fibrosis", pps_score: 45, kps_score: 50, admission_date: "2025-10-20", status: "active", location: "Home", primary_caregiver: "Son - John Davis" },
    { id: "p8", name: "Robert Johnson", diagnosis: "Metastatic Lung Cancer", pps_score: 45, kps_score: 50, admission_date: "2026-01-05", status: "active", location: "Home", primary_caregiver: "Wife - Carol Johnson" },
    { id: "p9", name: "Mary Williams", diagnosis: "End-stage Liver Disease", pps_score: 40, kps_score: 40, admission_date: "2025-11-10", status: "active", location: "Home", primary_caregiver: "Daughter - Jennifer Williams" },
    { id: "p10", name: "George Brown", diagnosis: "ALS", pps_score: 35, kps_score: 30, admission_date: "2025-08-15", status: "active", location: "Home", primary_caregiver: "Wife - Martha Brown" },
    { id: "p11", name: "Helen Garcia", diagnosis: "Metastatic Breast Cancer", pps_score: 50, kps_score: 50, admission_date: "2025-12-20", status: "active", location: "Home", primary_caregiver: "Husband - Luis Garcia" },
    { id: "p12", name: "Richard Miller", diagnosis: "End-stage Heart Failure", pps_score: 35, kps_score: 40, admission_date: "2025-09-01", status: "active", location: "ALF - Garden View", primary_caregiver: "Daughter - Karen Miller" },
  ];

  const generateDemoAIRecs = () => [
    { id: "ai-1", patient_name: "Eleanor Thompson", patient_id: "p1", type: "medication", recommendation: "Consider initiating continuous morphine infusion - current PRN dosing inadequate for escalating pain", priority: "high", rationale: "Pain scores averaging 8/10 over last 24 hours despite Q4H PRN morphine. Patient showing signs of uncontrolled pain.", confidence: 94 },
    { id: "ai-2", patient_name: "Harold Martinez", patient_id: "p2", type: "visit", recommendation: "Schedule additional RN visit within 24 hours - cardiac decompensation indicators present", priority: "high", rationale: "Weight gain of 4 lbs in 3 days, increased lower extremity edema noted. SpO2 trending down from 94% to 89%.", confidence: 91 },
    { id: "ai-3", patient_name: "Dorothy Anderson", patient_id: "p3", type: "care_plan", recommendation: "Update care plan to comfort measures only - significant decline in FAST score", priority: "medium", rationale: "FAST score declined from 7A to 7C. Minimal verbal response, requires total assistance with ADLs.", confidence: 88 },
    { id: "ai-4", patient_name: "William Taylor", patient_id: "p4", type: "medication", recommendation: "Add scheduled bronchodilator nebulizer treatments Q6H", priority: "medium", rationale: "Increasing dyspnea episodes, respiratory rate 24-28 at rest. Current PRN albuterol used 6x daily.", confidence: 86 },
    { id: "ai-5", patient_name: "George Brown", patient_id: "p10", type: "equipment", recommendation: "Order BiPAP for nocturnal respiratory support", priority: "medium", rationale: "ALS progression affecting respiratory function. FVC dropped to 45% of predicted. Patient reports morning headaches.", confidence: 89 },
    { id: "ai-6", patient_name: "Margaret White", patient_id: "p5", type: "visit", recommendation: "Consider GIP admission for pain crisis management", priority: "high", rationale: "Uncontrolled abdominal pain despite oral opioid escalation. Patient requesting hospitalization for comfort.", confidence: 92 },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "high": return "bg-amber-500/20 text-amber-400 border-amber-500/50";
      default: return "bg-slate-500/20 text-slate-400 border-slate-500/50";
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffHours = Math.floor((now - then) / (1000 * 60 * 60));
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-teal-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading Physician Command Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-slate-50 via-white to-slate-100'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDark ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-slate-200'} border-b backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/hos")} className={isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                  <Stethoscope className="w-6 h-6 text-teal-500" />
                </div>
                <div>
                  <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Physician Command Center</h1>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Unified Workflow Dashboard</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* WebSocket Connection Status */}
              <div 
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  wsConnected 
                    ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                    : isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                }`}
                title={wsConnected ? "Real-time updates active" : "Connecting to real-time updates..."}
              >
                {wsConnected ? (
                  <>
                    <Wifi className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Live</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Offline</span>
                  </>
                )}
              </div>
              
              {/* Notification Bell */}
              {newNotifications > 0 && (
                <div className="relative">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`relative ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                    onClick={() => setNewNotifications(0)}
                  >
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {newNotifications > 9 ? '9+' : newNotifications}
                    </span>
                  </Button>
                </div>
              )}
              
              {/* Notification Settings */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setSettingsDialog(true)}
                className={isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'}
                title="Notification Settings"
              >
                <Settings className="w-5 h-5" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className={`gap-2 ${isDark ? 'border-slate-600 text-slate-300' : 'border-slate-300'}`}>
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setTheme(isDark ? 'light' : 'dark')} className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} cursor-pointer hover:ring-2 hover:ring-teal-500/50 transition-all`} onClick={() => setActiveTab("orders")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Pending Orders</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{orders.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
              </div>
              {stats.urgentItems > 0 && (
                <Badge className="mt-2 bg-red-500/20 text-red-400 border-0 text-xs">
                  {stats.urgentItems} urgent
                </Badge>
              )}
            </CardContent>
          </Card>

          <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} cursor-pointer hover:ring-2 hover:ring-teal-500/50 transition-all`} onClick={() => setActiveTab("cti")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>CTI Pending</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{pendingCTIs.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                  <FileSignature className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} cursor-pointer hover:ring-2 hover:ring-teal-500/50 transition-all`} onClick={() => setActiveTab("f2f")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>F2F Due</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{f2fEncounters.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                  <UserCheck className="w-5 h-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} cursor-pointer hover:ring-2 hover:ring-teal-500/50 transition-all`} onClick={() => setActiveTab("patients")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Patients</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{patients.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <Users className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} cursor-pointer hover:ring-2 hover:ring-teal-500/50 transition-all`} onClick={() => setActiveTab("ai")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>AI Insights</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{aiRecommendations.length}</p>
                </div>
                <div className={`p-2 rounded-lg ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                  <Brain className="w-5 h-5 text-teal-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid grid-cols-6 mb-6 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
            <TabsTrigger value="overview" className="gap-2">
              <Home className="w-4 h-4" />
              <span className="hidden md:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden md:inline">Orders</span>
              {orders.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center">{orders.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="cti" className="gap-2">
              <FileSignature className="w-4 h-4" />
              <span className="hidden md:inline">CTI</span>
              {pendingCTIs.length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center">{pendingCTIs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="f2f" className="gap-2">
              <UserCheck className="w-4 h-4" />
              <span className="hidden md:inline">F2F</span>
            </TabsTrigger>
            <TabsTrigger value="patients" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden md:inline">Patients</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-2">
              <Brain className="w-4 h-4" />
              <span className="hidden md:inline">AI</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Urgent Items */}
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Requires Immediate Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {orders.filter(o => o.priority === "urgent").map(order => (
                        <div key={order.id} className={`p-3 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{order.patient_name}</p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{order.description}</p>
                              <Badge className="mt-2 bg-red-500/20 text-red-400 border-0 text-xs">URGENT</Badge>
                            </div>
                            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setSignDialog({ open: true, type: "order", item: order })}>
                              Sign
                            </Button>
                          </div>
                        </div>
                      ))}
                      {pendingCTIs.filter(c => c.days_until_due <= 5).map(cti => (
                        <div key={cti.id} className={`p-3 rounded-lg border ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{cti.patient_name}</p>
                              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{cti.type === "initial" ? "Initial Certification" : "Recertification"} - {cti.diagnosis}</p>
                              <Badge className="mt-2 bg-amber-500/20 text-amber-400 border-0 text-xs">Due in {cti.days_until_due} days</Badge>
                            </div>
                            <Button size="sm" variant="outline" className={isDark ? 'border-amber-500 text-amber-400' : 'border-amber-500 text-amber-600'} onClick={() => setCtiDialog({ open: true, cti })}>
                              Review
                            </Button>
                          </div>
                        </div>
                      ))}
                      {orders.filter(o => o.priority === "urgent").length === 0 && pendingCTIs.filter(c => c.days_until_due <= 5).length === 0 && (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No urgent items</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* AI Recommendations */}
              <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <CardHeader className="pb-3">
                  <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <Brain className="w-4 h-4 text-teal-500" />
                    AI Clinical Insights
                    <Badge className="bg-teal-500/20 text-teal-400 border-0 text-xs ml-2">
                      <Sparkles className="w-3 h-3 mr-1" />
                      GPT-5.2
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-3">
                      {aiRecommendations.map(rec => (
                        <div key={rec.id} className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${rec.priority === 'high' ? 'bg-red-500/20' : 'bg-teal-500/20'}`}>
                              {rec.type === 'medication' ? <Pill className={`w-4 h-4 ${rec.priority === 'high' ? 'text-red-400' : 'text-teal-400'}`} /> : <Calendar className={`w-4 h-4 ${rec.priority === 'high' ? 'text-red-400' : 'text-teal-400'}`} />}
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{rec.patient_name}</p>
                              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{rec.recommendation}</p>
                              <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'} italic`}>{rec.rationale}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <FileText className="w-5 h-5 text-blue-500" />
                  Pending Orders
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  Review and sign/reject pending medication, DME, and lab orders
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {orders.map(order => {
                      const OrderIcon = ORDER_TYPES[order.type]?.icon || FileText;
                      return (
                        <div key={order.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${ORDER_TYPES[order.type]?.bg || 'bg-slate-500/20'}`}>
                                <OrderIcon className={`w-5 h-5 ${ORDER_TYPES[order.type]?.color || 'text-slate-400'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{order.patient_name}</p>
                                  <Badge className={getPriorityColor(order.priority)} variant="outline">{order.priority}</Badge>
                                </div>
                                <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{order.description}</p>
                                <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                  Requested by {order.requested_by} • {formatTimeAgo(order.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="text-red-500 border-red-500/50 hover:bg-red-500/10" onClick={() => setSignDialog({ open: true, type: "reject", item: order })}>
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => setSignDialog({ open: true, type: "sign", item: order })}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Sign
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {orders.length === 0 && (
                      <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>All caught up!</p>
                        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No pending orders to review</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CTI Tab */}
          <TabsContent value="cti">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <FileSignature className="w-5 h-5 text-amber-500" />
                  Certification of Terminal Illness
                  <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs ml-2">CMS §418.22</Badge>
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  Initial certifications and recertifications pending your signature
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {pendingCTIs.map(cti => (
                      <div key={cti.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{cti.patient_name}</p>
                              <Badge className={cti.type === "initial" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"} variant="outline">
                                {cti.type === "initial" ? "Initial CTI" : `Recert (BP ${cti.benefit_period})`}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{cti.diagnosis}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                <Activity className="w-3 h-3 inline mr-1" />
                                PPS: {cti.pps_score}%
                              </span>
                              <span className={`text-xs ${cti.days_until_due <= 5 ? 'text-red-400' : isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                <Clock className="w-3 h-3 inline mr-1" />
                                Due in {cti.days_until_due} days
                              </span>
                            </div>
                          </div>
                          <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => setCtiDialog({ open: true, cti })}>
                            <PenLine className="w-4 h-4 mr-2" />
                            Certify
                          </Button>
                        </div>
                      </div>
                    ))}
                    {pendingCTIs.length === 0 && (
                      <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>All certifications complete</p>
                        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No pending CTIs</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* F2F Tab */}
          <TabsContent value="f2f">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <UserCheck className="w-5 h-5 text-purple-500" />
                  Face-to-Face Encounters
                  <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs ml-2">CMS §418.22(a)(4)</Badge>
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  F2F encounters due within 30 days of 3rd benefit period and beyond
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {f2fEncounters.map(f2f => (
                      <div key={f2f.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{f2f.patient_name}</p>
                            <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{f2f.diagnosis}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                Benefit Period {f2f.benefit_period}
                              </span>
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Due: {new Date(f2f.due_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <Button variant="outline" className={isDark ? 'border-purple-500 text-purple-400' : 'border-purple-500 text-purple-600'}>
                            <Calendar className="w-4 h-4 mr-2" />
                            Schedule F2F
                          </Button>
                        </div>
                      </div>
                    ))}
                    {f2fEncounters.length === 0 && (
                      <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                        <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>No F2F encounters due</p>
                        <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>All encounters are current</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patients Tab */}
          <TabsContent value="patients">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Users className="w-5 h-5 text-green-500" />
                  Patient Census
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  Your assigned hospice patients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search patients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`max-w-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-300'}`}
                  />
                </div>
                <ScrollArea className="h-[450px]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {patients.filter(p => p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())).map(patient => (
                      <div key={patient.id} className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{patient.name}</p>
                            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{patient.diagnosis}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="outline" className={`text-xs ${patient.pps_score <= 40 ? 'border-red-500 text-red-400' : 'border-green-500 text-green-400'}`}>
                                PPS {patient.pps_score}%
                              </Badge>
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                Admitted {new Date(patient.admission_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
                              <DropdownMenuItem className={isDark ? 'text-slate-300 focus:bg-slate-700' : ''}>
                                <FileText className="w-4 h-4 mr-2" />
                                View Chart
                              </DropdownMenuItem>
                              <DropdownMenuItem className={isDark ? 'text-slate-300 focus:bg-slate-700' : ''}>
                                <Pill className="w-4 h-4 mr-2" />
                                E-Prescribe
                              </DropdownMenuItem>
                              <DropdownMenuItem className={isDark ? 'text-slate-300 focus:bg-slate-700' : ''}>
                                <Phone className="w-4 h-4 mr-2" />
                                Call Family
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Tab */}
          <TabsContent value="ai">
            <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Brain className="w-5 h-5 text-teal-500" />
                  AI Clinical Decision Support
                  <Badge className="bg-teal-500/20 text-teal-400 border-0 text-xs ml-2">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Powered by GPT-5.2
                  </Badge>
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                  AI-generated recommendations based on patient data trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {aiRecommendations.map(rec => (
                      <div key={rec.id} className={`p-4 rounded-lg border ${isDark ? 'bg-gradient-to-r from-teal-500/10 to-purple-500/10 border-teal-500/30' : 'bg-gradient-to-r from-teal-50 to-purple-50 border-teal-200'}`}>
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg ${rec.priority === 'high' ? 'bg-red-500/20' : 'bg-teal-500/20'}`}>
                            <Brain className={`w-6 h-6 ${rec.priority === 'high' ? 'text-red-400' : 'text-teal-400'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{rec.patient_name}</p>
                              <Badge className={rec.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-teal-500/20 text-teal-400'}>{rec.priority} priority</Badge>
                            </div>
                            <p className={`${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{rec.recommendation}</p>
                            <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'} italic`}>
                              <Zap className="w-3 h-3 inline mr-1" />
                              {rec.rationale}
                            </p>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button size="sm" variant="outline" className={isDark ? 'border-slate-600' : ''}>
                                <XCircle className="w-4 h-4 mr-1" />
                                Dismiss
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Sign Order Dialog */}
      <Dialog open={signDialog.open} onOpenChange={(open) => !open && setSignDialog({ open: false, type: "", item: null })}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {signDialog.type === "sign" ? "Sign Order" : "Reject Order"}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              {signDialog.item?.patient_name} - {signDialog.item?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Notes (optional)</Label>
              <Textarea
                value={signNotes}
                onChange={(e) => setSignNotes(e.target.value)}
                placeholder={signDialog.type === "sign" ? "Add any notes..." : "Reason for rejection..."}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialog({ open: false, type: "", item: null })} className={isDark ? 'border-slate-600' : ''}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSignOrder(signDialog.item, signDialog.type === "sign" ? "sign" : "reject")}
              disabled={signLoading}
              className={signDialog.type === "sign" ? "bg-teal-600 hover:bg-teal-700" : "bg-red-600 hover:bg-red-700"}
            >
              {signLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              {signDialog.type === "sign" ? "Sign Order" : "Reject Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CTI Attestation Dialog */}
      <Dialog open={ctiDialog.open} onOpenChange={(open) => !open && setCtiDialog({ open: false, cti: null })}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <FileSignature className="w-5 h-5 text-amber-500" />
              Certification of Terminal Illness
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : ''}>
              {ctiDialog.cti?.patient_name} - {ctiDialog.cti?.diagnosis}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Patient Info */}
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Diagnosis:</span>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{ctiDialog.cti?.diagnosis}</p>
                  </div>
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>PPS Score:</span>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{ctiDialog.cti?.pps_score}%</p>
                  </div>
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Type:</span>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{ctiDialog.cti?.type === "initial" ? "Initial Certification" : `Recertification (BP ${ctiDialog.cti?.benefit_period})`}</p>
                  </div>
                  <div>
                    <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Due Date:</span>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>In {ctiDialog.cti?.days_until_due} days</p>
                  </div>
                </div>
              </div>

              {/* CMS Attestations */}
              <div>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>CMS Required Attestations</h4>
                <div className="space-y-3">
                  {CMS_ATTESTATION_ITEMS.map(item => (
                    <div key={item.id} className={`p-3 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-white border-slate-200'}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={item.id}
                          checked={attestations[item.id] || false}
                          onCheckedChange={(checked) => setAttestations(prev => ({ ...prev, [item.id]: checked }))}
                        />
                        <div className="flex-1">
                          <Label htmlFor={item.id} className={`font-medium cursor-pointer ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {item.label}
                            {item.required && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.description}</p>
                          <Badge variant="outline" className={`mt-1 text-xs ${isDark ? 'border-slate-600 text-slate-400' : ''}`}>{item.cms_ref}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Physician Notes */}
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Physician Narrative (optional)</Label>
                <Textarea
                  value={signNotes}
                  onChange={(e) => setSignNotes(e.target.value)}
                  placeholder="Additional clinical findings supporting terminal prognosis..."
                  className={`min-h-[100px] ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                />
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCtiDialog({ open: false, cti: null })} className={isDark ? 'border-slate-600' : ''}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSignCTI(ctiDialog.cti)}
              disabled={signLoading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {signLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <FileSignature className="w-4 h-4 mr-2" />}
              Sign & Certify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Settings className="w-5 h-5 text-teal-500" />
              Notification Preferences
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Customize which notifications you receive and when
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Notification Types */}
            <div className="space-y-4">
              <h4 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Notification Types
              </h4>
              
              {[
                { key: "orders", label: "New Orders", icon: FileText, description: "Order requests from nursing staff" },
                { key: "cti_requests", label: "CTI Requests", icon: FileSignature, description: "Certification of terminal illness" },
                { key: "f2f_encounters", label: "F2F Encounters", icon: UserCheck, description: "Face-to-face encounter reminders" },
                { key: "ai_recommendations", label: "AI Recommendations", icon: Brain, description: "AI-generated care insights" },
                { key: "patient_status", label: "Patient Status", icon: Activity, description: "Patient condition changes" }
              ].map(({ key, label, icon: Icon, description }) => (
                <div key={key} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-600' : 'bg-white'}`}>
                      <Icon className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{label}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{description}</p>
                    </div>
                  </div>
                  <Select
                    value={notificationPrefs[key]}
                    onValueChange={(value) => setNotificationPrefs(prev => ({ ...prev, [key]: value }))}
                  >
                    <SelectTrigger className={`w-32 h-8 text-xs ${isDark ? 'bg-slate-600 border-slate-500 text-white' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDark ? 'bg-slate-700 border-slate-600' : ''}>
                      <SelectItem value="all" className={isDark ? 'text-white hover:bg-slate-600' : ''}>All</SelectItem>
                      <SelectItem value="urgent_only" className={isDark ? 'text-white hover:bg-slate-600' : ''}>Urgent Only</SelectItem>
                      <SelectItem value="none" className={isDark ? 'text-white hover:bg-slate-600' : ''}>None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <Separator className={isDark ? 'bg-slate-700' : ''} />

            {/* Quiet Hours */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Quiet Hours
                  </h4>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Only urgent notifications during quiet hours
                  </p>
                </div>
                <Switch
                  checked={notificationPrefs.quiet_hours_enabled}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, quiet_hours_enabled: checked }))}
                />
              </div>

              {notificationPrefs.quiet_hours_enabled && (
                <div className={`p-3 rounded-lg space-y-3 ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Start</Label>
                      <Input
                        type="time"
                        value={notificationPrefs.quiet_hours_start}
                        onChange={(e) => setNotificationPrefs(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                        className={`h-8 text-sm ${isDark ? 'bg-slate-600 border-slate-500 text-white' : ''}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>End</Label>
                      <Input
                        type="time"
                        value={notificationPrefs.quiet_hours_end}
                        onChange={(e) => setNotificationPrefs(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                        className={`h-8 text-sm ${isDark ? 'bg-slate-600 border-slate-500 text-white' : ''}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Quiet all day on weekends
                    </Label>
                    <Switch
                      checked={notificationPrefs.quiet_weekends}
                      onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, quiet_weekends: checked }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator className={isDark ? 'bg-slate-700' : ''} />

            {/* Sound & Desktop Notifications */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {notificationPrefs.sound_enabled ? (
                    <Volume2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                  ) : (
                    <VolumeX className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                  )}
                  <Label className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Sound notifications
                  </Label>
                </div>
                <Switch
                  checked={notificationPrefs.sound_enabled}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, sound_enabled: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {notificationPrefs.desktop_notifications ? (
                    <Bell className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                  ) : (
                    <BellOff className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
                  )}
                  <Label className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Desktop notifications
                  </Label>
                </div>
                <Switch
                  checked={notificationPrefs.desktop_notifications}
                  onCheckedChange={(checked) => setNotificationPrefs(prev => ({ ...prev, desktop_notifications: checked }))}
                />
              </div>
            </div>

            <Separator className={isDark ? 'bg-slate-700' : ''} />

            {/* Browser Push Notification Permission */}
            <div className="space-y-3">
              <h4 className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Browser Push Notifications
              </h4>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Receive notifications even when this tab is in the background
              </p>
              
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {browserNotifPermission === "granted" ? (
                      <>
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                            Push Notifications Enabled
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            You will receive browser alerts for urgent updates
                          </p>
                        </div>
                      </>
                    ) : browserNotifPermission === "denied" ? (
                      <>
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                          <XCircle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                            Push Notifications Blocked
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            Enable in browser settings to receive alerts
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <Bell className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                            Push Notifications Not Enabled
                          </p>
                          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            Click to enable browser push notifications
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {browserNotifPermission !== "granted" && browserNotifPermission !== "denied" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={requestBrowserNotificationPermission}
                      className={`gap-2 ${isDark ? 'border-teal-500 text-teal-400 hover:bg-teal-500/20' : 'border-teal-600 text-teal-600 hover:bg-teal-50'}`}
                    >
                      <Bell className="w-4 h-4" />
                      Enable
                    </Button>
                  )}
                </div>
                
                {browserNotifPermission === "granted" && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <span className="font-medium">Notification types:</span> Urgent orders, CTI requests, F2F reminders, AI insights
                    </p>
                  </div>
                )}
                
                {browserNotifPermission === "denied" && (
                  <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      To enable: Click the lock icon in your browser address bar → Site settings → Notifications → Allow
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={resetNotificationPreferences}
              disabled={prefsLoading}
              className={`${isDark ? 'border-slate-600 text-slate-300' : ''}`}
            >
              Reset to Defaults
            </Button>
            <Button
              onClick={saveNotificationPreferences}
              disabled={prefsLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {prefsLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
